import { ampli } from '@/ampli';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { ClientInputError } from '@/lib/errors/client';

import { PAGE_SIZE as pageSize } from '@/components/History';
import { SearchBar } from '@/components/History/SearchBar';
import type { TradeDetails } from '@/components/Modal/TradeDetailsModal';
import {
  getOpenOrderColumns,
  OPEN_ORDER_COLUMN_CLASS,
} from '@/components/TradeHistoryTable/columns/openOrderColumns';
import { TableColumn } from '@/components/TradeHistoryTable/config';
import { DataTable } from '@/components/TradeHistoryTable/DataTable';
import { DegradedProvidersWarning } from '@/components/TradeHistoryTable/DegradedProvidersWarning';
import { TableSkeleton } from '@/components/TradeHistoryTable/TableSkeleton';
import { getTradeDetails } from '@/components/TradeHistoryTable/utils';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { useLastPage, usePagination } from '@/hooks/usePagination';
import { type LimitOrderFunders, useWalletOpenLimitOrders } from '@/hooks/useWalletOpenLimitOrders';

import { Pagination } from '@/ui/Pagination';

import type { LimitOrderItem } from '@/utils/0x.types';
import { trackError } from '@/utils/errors';
import { getLimitOrderIdentityKey } from '@/utils/limit-order-identity';
import { applyOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';

import { OpenOrdersNetworkSelect } from './OpenOrdersNetworkSelect';

interface OpenOrdersProps {
  funders: LimitOrderFunders;
  /** The current open order chain id, or null for all networks. */
  openOrderChainId: number | null;
  /** The function to handle open order chain id changes and update URL. */
  onOpenOrderChainChange: (chainId: number | null) => void;
  /** The function to set the selected trade details. */
  setSelectedTradeDetails: Dispatch<SetStateAction<TradeDetails | undefined>>;
}

const MAX_PER_PAGE = 1000;

// Columns for the loading skeleton; the live table is built from the shared
// open-order column factory below.
const SKELETON_COLUMNS: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'sellAmount', label: 'Sell' },
  { key: 'buyAmount', label: 'Buy' },
  { key: 'price', label: 'Price' },
  { key: 'expiry', label: 'Expiration' },
  { key: 'network', label: 'Network' },
  { key: 'status', label: 'Status' },
  { key: 'action', label: 'Actions' },
];

/**
 * Component to display open limit orders from the orderbook. Reads directly from
 * the `limit-orders` React Query cache (cancellations invalidate the cache and
 * write tombstones, so rows drop without a separate store mirror).
 */
export const OpenOrders = ({
  funders,
  openOrderChainId,
  onOpenOrderChainChange,
  setSelectedTradeDetails,
}: OpenOrdersProps) => {
  // vars
  const paginationProps = usePagination({ pageSize });
  // state
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  const {
    data: openOrdersData,
    isLoading: isLoadingOpenOrders,
    isRefetching: isRefetchingOpenOrders,
    refetch: refetchOpenOrders,
  } = useWalletOpenLimitOrders({
    funders,
    searchTerm,
    chainId: openOrderChainId ?? undefined,
    page: paginationProps.currentPage,
    limit: pageSize,
  });

  const { records, total } = useMemo(
    () =>
      openOrdersData ? applyOpenLimitOrderTombstones(openOrdersData) : { records: [], total: 0 },
    [openOrdersData],
  );

  const { lastPage } = useLastPage({
    totalItems: total,
    itemsPerPage: searchTerm ? MAX_PER_PAGE : pageSize,
  });

  const columns = useMemo(() => getOpenOrderColumns({ showPrice: true }), []);
  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getLimitOrderIdentityKey,
  });

  // events
  const handleSelectNetwork = (chainId: number | null) => {
    if (chainId === openOrderChainId) return;
    onOpenOrderChainChange(chainId);
  };

  const handleClickOpenOrderRow = (limitOrderRowItem: LimitOrderItem) => {
    const { makerToken, takerToken } = limitOrderRowItem;
    if (!limitOrderRowItem.makerTokenResult) {
      trackError(new ClientInputError('Maker token info not found in open orders'), { makerToken });
      return;
    }
    if (!limitOrderRowItem.takerTokenResult) {
      trackError(new ClientInputError('Taker token info not found in open orders'), { takerToken });
      return;
    }
    ampli.historyDetailViewed();
    setSelectedTradeDetails(getTradeDetails(limitOrderRowItem));
  };

  return (
    <>
      <section className="px-8 mb-4.5 w-full grid grid-cols-1 tablet:grid-cols-[auto_auto_1fr]">
        <SearchBar
          label="Search asset"
          placeholder="Search asset"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
        <OpenOrdersNetworkSelect chainId={openOrderChainId} onSelect={handleSelectNetwork} />
      </section>

      <DegradedProvidersWarning
        className="px-8 mb-4.5"
        degradedProviders={openOrdersData?.degradedProviders ?? []}
        isRetrying={isRefetchingOpenOrders}
        onRetry={() => void refetchOpenOrders()}
      />

      {isLoadingOpenOrders ? (
        <TableSkeleton numRows={pageSize} columns={SKELETON_COLUMNS} />
      ) : records.length > 0 ? (
        <>
          <DataTable
            table={table}
            columnClass={OPEN_ORDER_COLUMN_CLASS}
            onRowClick={handleClickOpenOrderRow}
          />

          {lastPage > 1 && (
            <div className="border-t-special-border-divider w-full border-t-1 py-4 tablet:pl-9">
              <Pagination {...paginationProps} lastPage={lastPage} />
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-26">
          {openOrderChainId === null
            ? 'No current open orders.'
            : `No current open orders on ${NETWORK_NAME_PER_CHAIN_ID[openOrderChainId]}.`}
        </div>
      )}
    </>
  );
};
